import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Button, Card, Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import { BASE_PATH, DASHBOARD_PATH } from 'config';
import { useDispatch } from 'store';
import { activeItem } from 'store/slices/menu';

import { gridSpacing } from 'store/constant';

// assets
import { IconTallymark1 } from '@tabler/icons';

import { HomeTwoTone, Home, KeyboardArrowLeft, AccountTreeTwoTone } from '@mui/icons-material';
import { NavItemType, NavItemTypeObject, OverrideIcon } from 'types';
import useGlobal from 'hooks/useGlobal';


const linkSX = {
    display: 'flex',
    color: 'grey.900',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center',
    '&:hover': {
        textDecoration: 'underline'
    },
   
   
};

interface PageNavigation {
    Title: string;
    BreadCrumbs?: { Title: string; Path: string, Tab?:string }[];
    MainPath?: string;
}

interface BreadCrumbSxProps extends React.CSSProperties {
    mb?: string;
    bgcolor?: string;
}

interface BreadCrumbsProps {
    card?: boolean;
    divider?: boolean;
    icon?: boolean;
    icons?: boolean;
    maxItems?: number;
    navigation?: NavItemTypeObject;
    rightAlign?: boolean;
    separator?: OverrideIcon;
    title?: boolean;
    titleBottom?: boolean;
    sx?: BreadCrumbSxProps;
    showTitle?: boolean;
}

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({
    card,
    divider,
    icon,
    icons,
    maxItems,
    navigation,
    rightAlign,
    separator,
    title,
    titleBottom,
    showTitle,
    ...others
}: BreadCrumbsProps) => {
    const theme = useTheme();
    
    const iconStyle = {
        marginRight: theme.spacing(0.75),
        marginTop: `-${theme.spacing(0.25)}`,
        width: '1rem',
        height: '1rem',
        color: theme.palette.secondary.main
    };

    const [brdCrmbs, setBrdCrmbs] = useState<NavItemType[] | undefined>();
    const [currNavItem, setCurrNavItem] = useState<NavItemType>();

    const location = useLocation();
    const navigate = useNavigate();
    const { accountId } = useParams();
    const { CurrentAccount,PageTitle,getLocalText } = useGlobal(); 

    const dispatch = useDispatch();

    const goBack = () => {
        if (brdCrmbs != undefined && brdCrmbs.length > 0 && brdCrmbs[brdCrmbs.length - 1].url) {
            const prev_page = brdCrmbs[brdCrmbs.length - 1].url;
            if (prev_page) {
                navigate(prev_page);
            }
        } else {
            navigate(-1);
        }
    };

    const accounts_brd:any = {
        Title: getLocalText("accounts"),
        Path: '/accounts/list'
    } ;

    const getBreadCrumbsByPath = (program: string) => {
        const path_parts = location.pathname.split('/').filter((p) => p.length > 0);

        if(program == 'activities' || program == 'mediamix'){
            // console.log(CurrentAccount , accountId)
            if(CurrentAccount && accountId && CurrentAccount.AccountId.toString() != accountId) {
                program += '_sub';
            }
        }

        let brdcrmbs: any[] = [];
        const l_state:any = location.state;
        switch (program) {
            case 'accounts_settings':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"),
                        Path: '/accounts/list'
                    }
                ];
                break;
                case 'subaccounts_settings':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"),
                        Path: '/accounts/list'
                    },
                    {
                        Title: CurrentAccount ? CurrentAccount.AccountName : 'Sub Accounts',
                        Path:`/accounts/${CurrentAccount && CurrentAccount.AccountId}/subAccounts/list`
                    }   
                ];
                break;
            case 'subaccounts':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"),
                        Path: '/accounts/list'
                    }  
                ];
                break;
            case 'activities':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"), // 'Accounts',
                        Path: '/accounts/list'
                    }  
                ];
                break;
                case 'activities_sub':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"), // 'Accounts',
                        Path: '/accounts/list'
                    },{
                        Title: CurrentAccount ? CurrentAccount.AccountName : 'Sub Accounts',
                        Path:`/accounts/${CurrentAccount && CurrentAccount.AccountId}/subAccounts/list`
                    }  
                ];
                break;
                case 'mediamix':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"), // 'Accounts',
                        Path: '/accounts/list'
                    },
                    {
                        Title: CurrentAccount ? CurrentAccount.AccountName : 'Activities',
                        Path: `${path_parts.slice(0, path_parts.length - 3).join('/')}/list` // '/accounts/:id/activities/list'
                    }, 
                ];
                
                if(l_state && l_state.breadcrumb){
                    if(l_state.breadcrumb.length == 2 && l_state.breadcrumb[0] =="tasks"){
                        brdcrmbs = [{
                            Title:  getLocalText("Tasks"),
                            Path: '/tasks/list',
                            Tab: l_state.breadcrumb[1]
                        }];
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="client_confirm"){
                        brdcrmbs = [{
                            Title:  getLocalText("Client Confirmations"),
                            Path: '/confirmations/list',
                        }];
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="media_serve"){
                        brdcrmbs = [{
                            Title:  getLocalText("Media Serve Report"),
                            Path: '/mediaServe/list',
                        }];
                    } 
                }
                break;
                case 'mediamix_sub':
                brdcrmbs = [
                    {
                        Title:  getLocalText("accounts"), // 'Accounts',
                        Path: '/accounts/list'
                    },
                    {
                        Title:CurrentAccount ? CurrentAccount.AccountName : 'Sub Accounts',
                        Path:`/accounts/${CurrentAccount && CurrentAccount.AccountId}/subAccounts/list`
                    },
                    {
                        Title: CurrentAccount && CurrentAccount.SubAccountName ? CurrentAccount.SubAccountName : 'Activities',
                        Path: `${path_parts.slice(0, path_parts.length - 3).join('/')}/list` // '/accounts/:id/activities/list'
                    }, 
                ];
                if(l_state && l_state.breadcrumb){
                    if(l_state.breadcrumb.length == 2) {
                        if(l_state.breadcrumb[0] =="tasks") {
                            brdcrmbs = [{
                                Title:  getLocalText("Tasks"),
                                Path: '/tasks/list',
                                Tab: l_state.breadcrumb[1]
                            }];
                        } else if(l_state.breadcrumb[0] =="monitoring") {
                            brdcrmbs = [{
                               Title:  getLocalText("Agency Status"),
                                Path: '/systemMonitoring',
                                Tab: l_state.breadcrumb[1]
                            }];
                        } 
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="client_confirm"){
                            brdcrmbs = [{
                                Title:  getLocalText("Client Confirmations"),
                                Path: '/confirmations/list',
                            }];
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="media_serve"){
                        brdcrmbs = [{
                            Title:  getLocalText("Media Serve Report"),
                            Path: '/mediaServe/list',
                        }];
                    } 
                }
                break;
            case 'mediamix_stats': {
                brdcrmbs = [{
                    Title:  getLocalText("performance"),
                    Path: '/performance/list'
                }];

                if(CurrentAccount && l_state && l_state.breadcrumb){
                    if(l_state.breadcrumb.length == 2 && l_state.breadcrumb[0] =="monitoring"){
                        brdcrmbs = [{
                            Title:  getLocalText("Agency Status"),
                            Path: '/systemMonitoring',
                            Tab: l_state.breadcrumb[1]
                        }];
                    } else if(l_state.breadcrumb.length == 2 && l_state.breadcrumb[1] == 'Activities'){
                        if(CurrentAccount.SubAccountId){
                            brdcrmbs = [
                                {
                                    Title:  getLocalText("accounts"), // 'Accounts',
                                    Path: '/accounts/list'
                                },
                                {
                                    Title: CurrentAccount.AccountName,
                                    Path: `/accounts/${CurrentAccount.AccountId}/subAccounts/list` 
                                },
                                {
                                    Title: CurrentAccount.SubAccountName,
                                    Path: `/accounts/${CurrentAccount.SubAccountId}/activities/list` 
                                }, 
                            ];
                        } else {
                            brdcrmbs = [
                                {
                                    Title:  getLocalText("accounts"), // 'Accounts',
                                    Path: '/accounts/list'
                                },
                                {
                                    Title: CurrentAccount ? CurrentAccount.AccountName : 'Activities',
                                    Path: `/accounts/${CurrentAccount?.AccountId}/activities/list` // '/accounts/:id/activities/list'
                                }, 
                            ];
                        }
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="billing"){
                        brdcrmbs = [{
                            Title:  getLocalText("Billing Report"),
                            Path: '/billing/list',
                        }];
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="client_confirm"){
                        brdcrmbs = [{
                            Title:  getLocalText("Client Confirmations"),
                            Path: '/confirmations/list',
                        }];
                    } else if(l_state.breadcrumb.length == 1 && l_state.breadcrumb[0] =="media_serve"){
                        brdcrmbs = [{
                            Title:  getLocalText("Media Serve Report"),
                            Path: '/mediaServe/list',
                        }];
                    } 
                }
                

                
                break;}
           case 'connectors':
            brdcrmbs = [
            {
                Title:  getLocalText("accounts"), // 'Accounts',
                Path: '/accounts/list'
            }];
            
            if(CurrentAccount && CurrentAccount.SubAccountId != undefined){
                // if its sub account connectors we add path to main account
                brdcrmbs.push({
                    Title: CurrentAccount.AccountName,
                    Path:`/accounts/${CurrentAccount.AccountId}/subAccounts/list`
                })
            }
            
           break;
            
        }
       // console.log('brd',CurrentAccount,brdcrmbs,PageTitle);
        return brdcrmbs;
    };

    const capitalizeTitle = (str: string) => {
        str = str.replaceAll('_', ' ');
        const splitStr = str.toLowerCase().split(' ');
        for (let i = 0; i < splitStr.length; i += 1) {
            // Assign it back to the array
            splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
        }
        // Directly return the joined string
        return splitStr.join(' ');
    };

    const SystemPrograms: { [key: string]: PageNavigation } = {
        '/': { Title: 'Back Stage' },
        accounts: {
            Title: 'Accounts',
            BreadCrumbs: [
                {
                    Title: 'Accounts',
                    Path: '/accounts/list'
                }
            ]
        },
        subaccounts: {
            Title: 'Sub Accounts',
            BreadCrumbs: getBreadCrumbsByPath('subaccounts')
        },
        accounts_settings: {
            Title: 'Account Settings',
            BreadCrumbs: getBreadCrumbsByPath('accounts_settings')
        },
        subaccounts_settings: {
            Title: 'Sub Account Settings',
            BreadCrumbs: getBreadCrumbsByPath('subaccounts_settings')
        },
        activities: {
            Title: 'Activities',
            BreadCrumbs: getBreadCrumbsByPath('activities')
        },
        mediamix: {
            Title: 'Media Plan',
            BreadCrumbs: getBreadCrumbsByPath('mediamix')
        },
        mediamix_stats: {
            Title: 'Activity Performance',
            BreadCrumbs: getBreadCrumbsByPath('mediamix_stats')
        },
        mediamix_approval: {
            Title: 'Media Plan Approval',
            BreadCrumbs: [
                {
                    Title: 'Pending Approvals',
                    Path: '/pendingApprovals/list'
                }
            ]
        },
        users_maint: {
            Title: 'User',
            BreadCrumbs: [
                {
                    Title: 'Users',
                    Path: '/users/list'
                }
            ]
        },
        channeltypes:{
            Title: 'Channel Types',
            BreadCrumbs: [accounts_brd]
        },
        account_connectors:{
            Title: 'Connectors',
            BreadCrumbs: getBreadCrumbsByPath('connectors')
            
        }
    };
    useEffect(() => {
        // we first check if path exist in menu
        // if not (inner page) we will set custom title&breadcrumbs
        getItemFromMenu();
        if (!itemFromMenu) {
            getCustomNavItem();
        }
    }, [location.pathname,CurrentAccount,PageTitle]);

    let itemFromMenu = false;
    const getItemFromMenu = () => {
        navigation?.items?.map((menu: NavItemType | NavItemTypeObject, index: number) => {
            if (menu.type && menu.type === 'group') {
                getCollapse(menu as { children: NavItemType[]; type?: string });
            }
            return false;
        });
    };

    const getCollapse = (menu: NavItemTypeObject) => {
        if (menu.children) {
            menu.children.filter((collapse: NavItemType) => {
                if (collapse.type && collapse.type === 'collapse') {
                    getCollapse(collapse as { children: NavItemType[]; type?: string });
                } else if (collapse.type && collapse.type === 'item') {
                    if (document.location.pathname === BASE_PATH + collapse.url) {
                        // setMain(menu);
                        setCurrNavItem(collapse);
                        // no breadcrmbs
                        setBrdCrmbs(undefined);
                        itemFromMenu = true;
                    }
                }
                return false;
            });
        }
    };

    const getCustomNavItem = () => {
        let curr_program = null;
        const path_parts = location.pathname.split('/').filter((p) => p.length > 0);
        if (path_parts && path_parts.length > 0) {
            let sys_program_key = '';
            if (path_parts.length > 1 && path_parts[path_parts.length - 1].toLowerCase() == 'list') {
                sys_program_key = path_parts[path_parts.length - 2].toLowerCase();
            }else if (path_parts.length > 1 && path_parts[path_parts.length - 1].toLowerCase() == 'stats') {
                sys_program_key = `${path_parts[path_parts.length - 3].toLowerCase()}_stats`;
            } else if (path_parts.length > 2 && path_parts[path_parts.length - 1].toLowerCase() == 'maint') {
                sys_program_key = `${path_parts[path_parts.length - 3].toLowerCase()}_maint`;
            } else if (path_parts.length > 3 && path_parts[path_parts.length - 2].toLowerCase() == 'maint') {
                sys_program_key = `${path_parts[path_parts.length - 4].toLowerCase()}_maint`;
            } else if (path_parts.length > 2 && path_parts[path_parts.length - 1].toLowerCase() == 'settings') {
                sys_program_key = `${path_parts[path_parts.length - 3].toLowerCase()}_settings`;
            }else if(path_parts.length > 1 && path_parts[path_parts.length - 1].toLowerCase() == 'approval'){
                sys_program_key = `${path_parts[path_parts.length - 3].toLowerCase()}_approval`;
            }else {
                sys_program_key = path_parts[0].toLowerCase();
            }
            
            if(sys_program_key == 'connectors' && path_parts[0] == 'accounts'){
                sys_program_key = 'account_connectors';
            }

            if (SystemPrograms[sys_program_key]) {
                curr_program = SystemPrograms[sys_program_key];
            } else {
                curr_program = {
                    Title: capitalizeTitle(sys_program_key)
                };
            }
        }
        if (curr_program) {
            const custom_nav_item: NavItemType = {
                title: curr_program.Title,
                breadcrumbs: true,
                url: curr_program.MainPath,
                type: 'item'
            };
            setCurrNavItem(custom_nav_item);

            if (curr_program.BreadCrumbs) {
                const custom_brdcrmbs = curr_program.BreadCrumbs.map((i) => ({ title: i.Title, url: i.Path, tab: i.Tab }));
                setBrdCrmbs(custom_brdcrmbs);
                // set active menu item
                setActiveMenuItem(custom_brdcrmbs[0].url.toLowerCase());
            } else {
                setBrdCrmbs(undefined);
                if (custom_nav_item.url) {
                    setActiveMenuItem(custom_nav_item.url.toLowerCase());
                }
            }
        } else {
            setBrdCrmbs(undefined);
        }
    };
    const setActiveMenuItem = (main_path: string) => {
        if (navigation && navigation.items) {
            for (let i = 0; i < navigation.items.length; i += 1) {
                const menu = navigation.items[i] as { children: NavItemType[]; type?: string };
                if (menu.type && menu.type === 'group') {
                    if (menu.children) {
                        for (let j = 0; j < menu.children.length; j += 1) {
                            const in_menu = menu.children[j];
                            if (in_menu.url && in_menu.url.toLowerCase() == main_path) {
                                dispatch(activeItem([in_menu.id!]));
                                //  console.log(in_menu);
                                break;
                            }
                        }
                    }
                }
            }
        }
    };

    // item separator
    const SeparatorIcon = separator!;
    const separatorIcon = separator ? <SeparatorIcon stroke={1.5} size="1rem" /> : <IconTallymark1 stroke={1.5} size="1rem" />;

    let mainContent;
    let itemContent;
    let breadcrumbContent: React.ReactElement = <Typography />;
    let itemTitle: NavItemType['title'] = '';
    // let CollapseIcon;
    let ItemIcon;

    // collapse item
    if (brdCrmbs && brdCrmbs.length > 0) {
        // CollapseIcon = main.icon ? main.icon : AccountTreeTwoTone;
        mainContent = brdCrmbs.map((m, i) => (
            <Typography key={i} component={Link} sx={{textTransform: 'capitalize',...linkSX}}
            to={m.url || '#'} 
            variant="subtitle1" 
            state={m.tab ? { tab: [m.tab] }: undefined} // anything you want
           >
                {icons && <AccountTreeTwoTone style={iconStyle} />}
                {m.title}
            </Typography>
        ));
    }

    // items
    if (currNavItem && currNavItem.type === 'item') {
        itemTitle = currNavItem && currNavItem.title;
        
        ItemIcon = currNavItem && currNavItem.icon ? currNavItem.icon : AccountTreeTwoTone;
        itemContent = (
            <Typography
                variant="subtitle1"
                sx={{
                    textTransform: 'capitalize',
                    display: 'flex',
                    textDecoration: 'none',
                    alignContent: 'center',
                    alignItems: 'center',
                    fontWeight:'bold',
                    color: theme.palette.secondary.main
                }}
            >
                {icons && <ItemIcon style={iconStyle} />}
                {PageTitle || itemTitle  }
            </Typography>
        );

        // main
        if (currNavItem && currNavItem.breadcrumbs !== false) {
            breadcrumbContent = (
                <Card
                    sx={{
                        marginBottom: card === false ? 0 : theme.spacing(gridSpacing.xs),
                        border: card === false ? 'none' : '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[200],
                        background: card === false ? theme.palette.background.default : theme.palette.grey[100]
                    }}
                    {...others}
                >
                    <Box sx={{ px: 2,py:1, pl: card === false ? 0 : 2 }}>
                        <Grid
                            container
                            direction={rightAlign ? 'row' : 'column'}
                            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
                            alignItems={rightAlign ? 'center' : 'flex-start'}
                            spacing={1}
                        >
                            {showTitle && title && !titleBottom && (
                                <Grid item>
                                    <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                        <Button sx={{ p: 0, mr: 1, minWidth: 0,verticalAlign:'text-bottom' }} onClick={goBack}>
                                          <KeyboardArrowLeft />
                                        </Button>
                                        {currNavItem.title}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item>
                                <MuiBreadcrumbs
                                    sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
                                    aria-label="breadcrumb"
                                    maxItems={maxItems || 8}
                                    separator={separatorIcon}
                                >
                                    <Typography component={Link} to={DASHBOARD_PATH} color="inherit" variant="subtitle1" sx={linkSX}>
                                        {icons && <HomeTwoTone sx={iconStyle} />}
                                        {icon && <Home sx={{ ...iconStyle, mr: 0 }} />}
                                        {!icon && 'Dashboard'}
                                    </Typography>
                                    {mainContent}
                                    {itemContent}
                                </MuiBreadcrumbs>
                            </Grid>
                            {/* <Grid item>
                                <CurrentAccountTag />
                            </Grid> */}
                            {title && titleBottom && (
                                <Grid item>
                                    <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                        {currNavItem.title}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    {card === false && divider !== false && <Divider sx={{ borderColor: theme.palette.primary.main, mb: gridSpacing }} />}
                </Card>
            );
        }
    }

    return breadcrumbContent;
};

export default Breadcrumbs;
